// src/components/EnhancedBookingsTable.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { updateBookingChecks, updateBookingEquipment, updateBookingNotes, updateBooking } from '../services/bookingService';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './EnhancedBookingsTable.css';

/**
 * @typedef {Object} ClassTypeStyle
 * @property {string} detailsClass - CSS class for details section
 * @property {string} badgeClass - CSS class for badge
 * @property {string} label - Label text
 */

// Add highlight color logic from BookingHistory
const classHighlightColors = {
  'cert3': 'rgba(0, 184, 212, 0.10)', // cyan
  'cert4': 'rgba(61, 124, 245, 0.10)', // blue
  'diploma': 'rgba(255, 193, 7, 0.10)', // yellow
  'default': 'rgba(61,124,245,0.08)'
};

const staffEditHighlightBg = 'rgba(245, 158, 11, 0.14)';
const staffEditHighlightBorder = '#f59e0b';

const toEquipmentMap = (items) => {
  const map = new Map();
  if (!Array.isArray(items)) return map;
  items.forEach((item) => {
    const equipmentId = item?.equipmentId;
    if (!equipmentId) return;
    map.set(equipmentId, {
      equipmentId,
      equipmentName: item?.equipmentName || 'Unknown equipment',
      quantity: Number(item?.quantity || 0)
    });
  });
  return map;
};

const diffEquipmentItems = (oldItems, newItems) => {
  const oldMap = toEquipmentMap(oldItems);
  const newMap = toEquipmentMap(newItems);
  const added = [];
  const removed = [];
  const qtyChanged = [];

  newMap.forEach((nextItem, equipmentId) => {
    if (!oldMap.has(equipmentId)) {
      added.push(nextItem);
      return;
    }

    const oldItem = oldMap.get(equipmentId);
    if (oldItem.quantity !== nextItem.quantity) {
      qtyChanged.push({
        equipmentId,
        equipmentName: nextItem.equipmentName || oldItem.equipmentName,
        oldQuantity: oldItem.quantity,
        newQuantity: nextItem.quantity
      });
    }
  });

  oldMap.forEach((oldItem, equipmentId) => {
    if (!newMap.has(equipmentId)) {
      removed.push(oldItem);
    }
  });

  return { added, removed, qtyChanged };
};

// The main component for displaying bookings with categorized equipment
const EnhancedBookingsTable = ({ 
  bookings = [], // Add default empty array here
  studentId, 
  isStaff, 
  equipmentData = [], // Add a default empty array here
  approveBooking, 
  returnEquipment,
  onBookingUpdated,
  displayMode = 'current', // New prop to handle current/previous display mode
  rejectBooking, // Add this prop
  customHeader = undefined, // make optional
  tableId = 'bookings-table', // Add tableId prop with default value
  onStudentSaveBooking,
  onMarkBookingEditsReviewed
}) => {
  // State to track checked out and checked in equipment
  const [checkedOutItems, setCheckedOutItems] = useState({});
  const [checkedInItems, setCheckedInItems] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeNotes, setActiveNotes] = useState(null); // State to track which notes popup is active
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [modifiedItems, setModifiedItems] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState([]);
  const [pendingAdd, setPendingAdd] = useState([]);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [expandedBookings, setExpandedBookings] = useState({});
  const [editingDates, setEditingDates] = useState(null);
  const [dateDraft, setDateDraft] = useState({ startDate: '', endDate: '' });
  const [studentEditBookingId, setStudentEditBookingId] = useState(null);
  const [studentEditDraft, setStudentEditDraft] = useState(null);
  const [studentEditError, setStudentEditError] = useState('');
  const [studentAddEquipmentId, setStudentAddEquipmentId] = useState('');
  const [studentEquipmentSearch, setStudentEquipmentSearch] = useState('');

  const normalizeSearchText = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Replace noteButtonRefs = {} with useRef
  const noteButtonRefs = useRef({});
  const [notePopupPos, setNotePopupPos] = useState({ left: 0, top: 0 });

  // Filter bookings based on display mode
  const filteredBookings = React.useMemo(() => {
    if (!Array.isArray(bookings)) return [];
    
    return bookings.filter(booking => {
      if (!booking) return false;
      
      const isReturned = booking.status === 'Returned' && booking.returnedDate;
      
      if (displayMode === 'current') {
        // For current bookings, show all non-returned bookings
        return !isReturned;
      } else {
        // For previous bookings, show only returned bookings
        return isReturned;
      }
    });
  }, [bookings, displayMode]);

  /**
   * Function to determine class type and return appropriate CSS class
   * @param {string} className - The class name to check
   * @returns {ClassTypeStyle} Object containing CSS classes and label
   */
  const getClassTypeStyles = (className) => {
    if (!className) return {
      detailsClass: '',
      badgeClass: '',
      label: ''
    };
    
    const lowerClassName = className.toLowerCase();
    
    if (lowerClassName.includes('cert 3') || lowerClassName.includes('cert3')) {
      return {
        detailsClass: 'cert3-class',
        badgeClass: 'cert3-badge',
        label: 'Cert 3'
      };
    } else if (lowerClassName.includes('cert 4') || lowerClassName.includes('cert4')) {
      return {
        detailsClass: 'cert4-class',
        badgeClass: 'cert4-badge',
        label: 'Cert 4'
      };
    } else if (lowerClassName.includes('diploma')) {
      return {
        detailsClass: 'diploma-class',
        badgeClass: 'diploma-badge',
        label: 'Diploma'
      };
    }
    
    return {
      detailsClass: '',
      badgeClass: '',
      label: ''
    };
  };

  // Create mapping of equipment ID to its category and type
  const equipmentCategories = {};
  
  // Only try to process equipmentData if it exists and is an array
  if (Array.isArray(equipmentData)) {
    equipmentData.forEach(item => {
      if (item && item.id) {
        equipmentCategories[item.id] = {
          category: item.category,
          type: item.type
        };
      }
    });
  }

  // Initialize checkbox states from bookings data
  useEffect(() => {
    if (!Array.isArray(bookings) || bookings.length === 0) return;
    
    const initialCheckedOutItems = {};
    const initialCheckedInItems = {};
    
    bookings.forEach(booking => {
      if (!booking || !booking.id) return;
      
      if (booking.checkedOutItems) {
        initialCheckedOutItems[booking.id] = booking.checkedOutItems;
      }
      
      if (booking.checkedInItems) {
        initialCheckedInItems[booking.id] = booking.checkedInItems;
      }
    });
    
    setCheckedOutItems(initialCheckedOutItems);
    setCheckedInItems(initialCheckedInItems);
  }, [bookings]);

  // Add click handler to close notes popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeNotes && !event.target.closest('.notes-popup') && 
          !event.target.closest('.notes-button')) {
        setActiveNotes(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeNotes]);

  // Save edited notes (for both staff and students)
  const handleSaveNotes = async (bookingId) => {
    if (!isStaff) {
      return;
    }

    try {
      const notesResult = await updateBookingNotes(bookingId, notesDraft, true);
      
      if (notesResult.error) {
        return;
      }

      const updatedBookings = bookings.map(b => 
        b.id === bookingId 
          ? { ...b, notes: notesDraft, notesLastUpdated: new Date().toISOString() }
          : b
      );
      
      onBookingUpdated(updatedBookings);
      setEditingNotes(null);
      setNotesDraft("");
      setActiveNotes(null); // Close the notes popup after saving
    } catch (error) {
      // Handle error silently
    }
  };

  // Toggle notes popup
  const toggleNotes = (bookingId) => {
    if (activeNotes === bookingId) {
      setActiveNotes(null);
      setEditingNotes(null);
      setNotesDraft("");
    } else {
      setActiveNotes(bookingId);
      const booking = bookings.find(b => b.id === bookingId);
      if (isStaff && booking && !String(booking.notes || '').trim()) {
        setEditingNotes(bookingId);
        setNotesDraft('');
      }
      
      // If student is viewing notes and they were updated by staff, clear the flag immediately
      if (!isStaff) {
        if (booking && booking.notesUpdatedByStaff) {
          // Update local state immediately
          const updatedBookings = bookings.map(b => {
            if (b.id === bookingId) {
              return { ...b, notesUpdatedByStaff: false };
            }
            return b;
          });
          
          if (onBookingUpdated) {
            onBookingUpdated(updatedBookings);
          }
          
          // Update Firestore in the background
          updateBooking(bookingId, { notesUpdatedByStaff: false })
            .catch(error => {
              console.error("Error updating notes flag:", error);
              // Revert local state if Firestore update fails
              const revertedBookings = bookings.map(b => {
                if (b.id === bookingId) {
                  return { ...b, notesUpdatedByStaff: true };
                }
                return b;
              });
              
              if (onBookingUpdated) {
                onBookingUpdated(revertedBookings);
              }
            });
        }
      }
    }
  };

  // Group booking items by category
  const groupBookingItemsByCategory = (bookingItems) => {
    if (!Array.isArray(bookingItems) || bookingItems.length === 0) {
      return {}; // Return empty object if no items
    }
    
    const groupedItems = {};
    
    bookingItems.forEach(item => {
      if (!item || !item.equipmentId) return;
      
      const equipInfo = equipmentCategories[item.equipmentId];
      if (equipInfo) {
        const category = equipInfo.category;
        if (!groupedItems[category]) {
          groupedItems[category] = [];
        }
        groupedItems[category].push({
          ...item,
          category: category,
          type: equipInfo.type
        });
      } else {
        // Fallback for items that don't have category info
        if (!groupedItems['Other']) {
          groupedItems['Other'] = [];
        }
        groupedItems['Other'].push(item);
      }
    });
    
    return groupedItems;
  };
  
  // Save checkout/checkin status to Firestore
  const handleSaveCheckStatus = async (bookingId, status) => {
    try {
      await updateBookingChecks(bookingId, status);
      // Refresh bookings after status update
      onBookingUpdated([...bookings]);
    } catch (error) {
      // Handle error silently
    }
  };

  // Handler for checking out equipment
  const handleCheckout = async (bookingId, equipmentId) => {
    try {
      // Update local state first
      const newCheckedOut = { ...checkedOutItems };
      if (!newCheckedOut[bookingId]) {
        newCheckedOut[bookingId] = {};
      }
      // Toggle the checked out state
      newCheckedOut[bookingId][equipmentId] = !newCheckedOut[bookingId][equipmentId];
      setCheckedOutItems(newCheckedOut);

      // Then make API call
      await updateBookingEquipment(bookingId, equipmentId, 'checkout');
      
      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => {
        if (booking.id === bookingId) {
          return {
            ...booking,
            checkedOutItems: {
              ...booking.checkedOutItems,
              [equipmentId]: newCheckedOut[bookingId][equipmentId]
            }
          };
        }
        return booking;
      });
      
      if (onBookingUpdated) {
        onBookingUpdated(updatedBookings);
      }
    } catch (error) {
      // Revert local state on error
      const newCheckedOut = { ...checkedOutItems };
      if (newCheckedOut[bookingId]) {
        delete newCheckedOut[bookingId][equipmentId];
      }
      setCheckedOutItems(newCheckedOut);
    }
  };

  // Handler for checking in equipment
  const handleCheckin = async (bookingId, equipmentId) => {
    try {
      // Update local state first
      const newCheckedIn = { ...checkedInItems };
      if (!newCheckedIn[bookingId]) {
        newCheckedIn[bookingId] = {};
      }
      // Toggle the checked in state
      newCheckedIn[bookingId][equipmentId] = !newCheckedIn[bookingId][equipmentId];
      setCheckedInItems(newCheckedIn);

      // Then make API call
      await updateBookingEquipment(bookingId, equipmentId, 'checkin');
      
      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => {
        if (booking.id === bookingId) {
          return {
            ...booking,
            checkedInItems: {
              ...booking.checkedInItems,
              [equipmentId]: newCheckedIn[bookingId][equipmentId]
            }
          };
        }
        return booking;
      });
      
      if (onBookingUpdated) {
        onBookingUpdated(updatedBookings);
      }
    } catch (error) {
      // Revert local state on error
      const newCheckedIn = { ...checkedInItems };
      if (newCheckedIn[bookingId]) {
        delete newCheckedIn[bookingId][equipmentId];
      }
      setCheckedInItems(newCheckedIn);
    }
  };

  // Get check-out status for an item
  const isCheckedOut = (bookingId, equipmentId) => {
    return !!(checkedOutItems?.[bookingId]?.[equipmentId]);
  };

  // Get check-in status for an item
  const isCheckedIn = (bookingId, equipmentId) => {
    return !!(checkedInItems?.[bookingId]?.[equipmentId]);
  };

  // Handle empty bookings state with appropriate message based on displayMode
  const getEmptyMessage = () => {
    if (!Array.isArray(bookings)) {
      return "Loading bookings...";
    }
    
    if (filteredBookings.length === 0) {
      if (isStaff) {
        return "No bookings to display";
      } else {
        return displayMode === 'current' 
          ? "You have no current bookings" 
          : "You have no previous bookings";
      }
    }
    
    return "";
  };

  // Handle equipment modification
  const handleModifyEquipment = (booking) => {
    setEditingEquipment(booking.id);
    setModifiedItems([...booking.items]);
    setPendingRemoval([]);
    setPendingAdd([]);
  };

  // Handle equipment quantity change
  const handleEquipmentQuantityChange = (equipmentId, newQuantity) => {
    if (!modifiedItems) return;

    const updatedItems = modifiedItems.map(item => {
      if (item.equipmentId === equipmentId) {
        return { ...item, quantity: Math.max(0, newQuantity) };
      }
      return item;
    });

    setModifiedItems(updatedItems);
  };

  // Handle adding new equipment
  const handleAddEquipment = (equipmentId) => {
    if (!modifiedItems) return;

    const equipment = equipmentData.find(e => e.id === equipmentId);
    if (!equipment) return;

    const existingItem = modifiedItems.find(item => item.equipmentId === equipmentId);
    if (existingItem) return;

    setModifiedItems([
      ...modifiedItems,
      {
        equipmentId: equipment.id,
        equipmentName: equipment.name,
        quantity: 1,
        maxQuantity: equipment.availableQty
      }
    ]);
    setPendingAdd([...pendingAdd, equipment.id]);
  };

  // Handle removing equipment
  const handleRemoveEquipment = (equipmentId) => {
    if (!modifiedItems) return;
    
    // Add to pending removal for UI feedback
    setPendingRemoval([...pendingRemoval, equipmentId]);
  };

  // Save equipment modifications
  const handleSaveEquipmentModifications = async (bookingId) => {
    if (!modifiedItems) return;
    
    try {
      setIsSaving(true);
      
      // Filter out items that are pending removal
      const finalItems = modifiedItems.filter(item => !pendingRemoval.includes(item.equipmentId));
      
      // Update the booking with modified items
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        items: finalItems,
        updatedAt: serverTimestamp()
      });

      // Update local state
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingId 
          ? { ...booking, items: finalItems }
          : booking
      );
      
      if (onBookingUpdated) {
        onBookingUpdated(updatedBookings);
      }

      // Reset modification state
      setEditingEquipment(null);
      setModifiedItems(null);
      setPendingRemoval([]);
      setPendingAdd([]);
    } catch (error) {
      console.error('Error saving equipment modifications:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel equipment modifications
  const cancelEquipmentModifications = () => {
    setEditingEquipment(null);
    setModifiedItems(null);
    setPendingRemoval([]);
    setPendingAdd([]);
  };

  // Helper to toggle expanded state
  const toggleBookingExpand = (bookingId) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (isStaff && booking?.hasUnreviewedEdits && typeof onMarkBookingEditsReviewed === 'function') {
      onMarkBookingEditsReviewed(bookingId);
    }
    setExpandedBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  const startStudentEdit = (booking) => {
    setStudentEditError('');
    setStudentEditBookingId(booking.id);
    setStudentAddEquipmentId('');
    setStudentEquipmentSearch('');
    setStudentEditDraft({
      startDate: booking.startDate,
      endDate: booking.endDate,
      notes: booking.notes || '',
      items: (booking.items || []).map((item) => ({ ...item }))
    });
  };

  const cancelStudentEdit = () => {
    setStudentEditBookingId(null);
    setStudentEditDraft(null);
    setStudentEditError('');
    setStudentAddEquipmentId('');
    setStudentEquipmentSearch('');
  };

  const handleStudentDraftChange = (field, value) => {
    setStudentEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleStudentItemQuantityChange = (equipmentId, quantity) => {
    setStudentEditDraft((prev) => ({
      ...prev,
      items: prev.items.map((item) => (
        item.equipmentId === equipmentId
          ? { ...item, quantity: Math.max(1, Number(quantity || 1)) }
          : item
      ))
    }));
  };

  const canStudentEditBooking = (booking) => {
    if (!booking || !booking.startDate) return false;
    if (booking.status === 'Returned' || booking.status === 'Rejected') return false;
    const bookingStart = new Date(`${booking.startDate}T12:00:00`);
    if (Number.isNaN(bookingStart.getTime())) return false;
    const editCutoff = new Date(bookingStart.getTime() - (24 * 60 * 60 * 1000));
    return new Date() < editCutoff;
  };

  const handleStudentRemoveItem = (equipmentId) => {
    setStudentEditDraft((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      if (prev.items.length <= 1) {
        setStudentEditError('At least one equipment item is required.');
        return prev;
      }
      setStudentEditError('');
      return {
        ...prev,
        items: prev.items.filter((item) => item.equipmentId !== equipmentId)
      };
    });
  };

  const handleStudentAddItem = () => {
    if (!studentAddEquipmentId || !studentEditDraft) return;
    const equipmentToAdd = (equipmentData || []).find((item) => item.id === studentAddEquipmentId);
    if (!equipmentToAdd) return;

    if ((studentEditDraft.items || []).some((item) => item.equipmentId === equipmentToAdd.id)) {
      setStudentEditError('This equipment is already in the booking.');
      return;
    }

    setStudentEditError('');
    setStudentEditDraft((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        {
          equipmentId: equipmentToAdd.id,
          equipmentName: equipmentToAdd.name,
          quantity: 1
        }
      ]
    }));
    setStudentAddEquipmentId('');
    setStudentEquipmentSearch('');
  };

  const studentAddEquipmentGroups = React.useMemo(() => {
    const existingIds = new Set((studentEditDraft?.items || []).map((item) => item.equipmentId));
    const search = normalizeSearchText(studentEquipmentSearch);
    const searchTokens = search ? search.split(' ') : [];

    const filtered = (equipmentData || []).filter((item) => {
      if (!item?.id || existingIds.has(item.id)) return false;
      if (!search) return true;

      const haystack = normalizeSearchText([
        item.name,
        item.type,
        item.category
      ].join(' '));
      if (!haystack) return false;
      return searchTokens.every((token) => haystack.includes(token));
    });

    const grouped = filtered.reduce((acc, item) => {
      const category = item.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => ({
        category,
        items: items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      }));
  }, [equipmentData, studentEditDraft, studentEquipmentSearch]);

  const studentAddEquipmentFlatList = React.useMemo(
    () => studentAddEquipmentGroups.flatMap((group) => group.items),
    [studentAddEquipmentGroups]
  );

  useEffect(() => {
    if (!studentEditBookingId) return;
    if (!studentAddEquipmentId && studentAddEquipmentFlatList.length > 0) {
      setStudentAddEquipmentId(studentAddEquipmentFlatList[0].id);
      return;
    }
    if (studentAddEquipmentId && !studentAddEquipmentFlatList.some((item) => item.id === studentAddEquipmentId)) {
      setStudentAddEquipmentId(studentAddEquipmentFlatList[0]?.id || '');
    }
  }, [studentEditBookingId, studentAddEquipmentId, studentAddEquipmentFlatList]);

  const saveStudentEdit = async (booking) => {
    if (!onStudentSaveBooking || !studentEditDraft) return;
    setIsSaving(true);
    setStudentEditError('');
    const result = await onStudentSaveBooking(booking.id, studentEditDraft, booking);
    setIsSaving(false);

    if (!result?.success) {
      setStudentEditError(result?.error || 'Failed to save changes.');
      return;
    }

    cancelStudentEdit();
  };

  // Update popup position when activeNotes changes
  useEffect(() => {
    if (activeNotes && noteButtonRefs.current[activeNotes]) {
      const rect = noteButtonRefs.current[activeNotes].getBoundingClientRect();
      setNotePopupPos({
        left: rect.left,
        top: rect.bottom + window.scrollY
      });
    }
  }, [activeNotes]);

  // Handle date editing
  const handleEditDates = (booking) => {
    setEditingDates(booking.id);
    setDateDraft({
      startDate: booking.startDate,
      endDate: booking.endDate
    });
  };

  // Handle date changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateDraft(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save date changes
  const handleSaveDates = async (bookingId) => {
    try {
      setIsSaving(true);
      
      // Validate dates
      const startDate = new Date(dateDraft.startDate);
      const endDate = new Date(dateDraft.endDate);
      
      if (startDate > endDate) {
        alert('End date must be after start date');
        return;
      }

      // Update the booking with new dates
      const response = await updateBooking(bookingId, {
        startDate: dateDraft.startDate,
        endDate: dateDraft.endDate
      });

      if (response.success) {
        // Update local state
        const updatedBookings = bookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, startDate: dateDraft.startDate, endDate: dateDraft.endDate }
            : booking
        );
        
        if (onBookingUpdated) {
          onBookingUpdated(updatedBookings);
        }
        
        setEditingDates(null);
        setDateDraft({ startDate: '', endDate: '' });
      }
    } catch (error) {
      console.error('Error saving date changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel date editing
  const cancelDateEditing = () => {
    setEditingDates(null);
    setDateDraft({ startDate: '', endDate: '' });
  };

  const getAddedEquipmentIds = (booking) => {
    if (!Array.isArray(booking?.changeLog)) return new Set();
    const ids = new Set();

    booking.changeLog.forEach((entry) => {
      if (entry?.field === 'equipmentAdded' && entry?.newValue?.equipmentId) {
        ids.add(entry.newValue.equipmentId);
      }

      if (entry?.field === 'items') {
        const { added } = diffEquipmentItems(entry?.oldValue, entry?.newValue);
        added.forEach((item) => ids.add(item.equipmentId));
      }
    });

    return ids;
  };

  const renderChangeEntry = (entry) => {
    const field = entry?.field;
    const oldValue = entry?.oldValue;
    const newValue = entry?.newValue;
    const timestamp = entry?.timestamp ? new Date(entry.timestamp).toLocaleString('en-GB') : 'Unknown time';
    const studentName = entry?.studentName || 'Student';

    const wrap = (label, detail, highlight = false) => (
      <div
        style={{
          marginBottom: 8,
          padding: 8,
          border: `1px solid ${highlight ? staffEditHighlightBorder : '#444'}`,
          borderRadius: 6,
          background: highlight ? staffEditHighlightBg : 'transparent'
        }}
      >
        <div><strong>{label}</strong> by {studentName}</div>
        <div style={{ fontSize: '0.9rem', color: '#aaa' }}>{timestamp}</div>
        <div style={{ fontSize: '0.95rem' }}>{detail}</div>
      </div>
    );

    if (field === 'equipmentAdded') {
      return wrap(
        'Equipment Added',
        `${newValue?.equipmentName || 'Equipment'} (Qty ${newValue?.quantity || 0})`,
        true
      );
    }

    if (field === 'equipmentRemoved') {
      return wrap(
        'Equipment Removed',
        `${oldValue?.equipmentName || 'Equipment'} (Qty ${oldValue?.quantity || 0})`,
        true
      );
    }

    if (field === 'equipmentQtyChanged') {
      return wrap(
        'Equipment Quantity Updated',
        `${newValue?.equipmentName || oldValue?.equipmentName || 'Equipment'}: ${oldValue?.quantity || 0} -> ${newValue?.quantity || 0}`,
        true
      );
    }

    if (field === 'startDate' || field === 'endDate') {
      return wrap(
        field === 'startDate' ? 'Start Date Updated' : 'End Date Updated',
        `${String(oldValue || 'n/a')} -> ${String(newValue || 'n/a')}`
      );
    }

    if (field === 'notes') {
      const oldNotes = String(oldValue || '').trim();
      const newNotes = String(newValue || '').trim();
      const oldPreview = oldNotes ? `"${oldNotes.slice(0, 80)}${oldNotes.length > 80 ? '...' : ''}"` : '(empty)';
      const newPreview = newNotes ? `"${newNotes.slice(0, 80)}${newNotes.length > 80 ? '...' : ''}"` : '(empty)';
      return wrap('Notes Updated', `${oldPreview} -> ${newPreview}`);
    }

    if (field === 'items') {
      const { added, removed, qtyChanged } = diffEquipmentItems(oldValue, newValue);
      const parts = [];

      if (added.length > 0) {
        parts.push(`Added: ${added.map((item) => `${item.equipmentName} (Qty ${item.quantity})`).join(', ')}`);
      }
      if (removed.length > 0) {
        parts.push(`Removed: ${removed.map((item) => `${item.equipmentName} (Qty ${item.quantity})`).join(', ')}`);
      }
      if (qtyChanged.length > 0) {
        parts.push(`Qty changed: ${qtyChanged.map((item) => `${item.equipmentName} ${item.oldQuantity} -> ${item.newQuantity}`).join(', ')}`);
      }

      const details = parts.length > 0 ? parts.join(' | ') : 'Student updated booking equipment.';
      return wrap('Equipment Updated', details, added.length > 0 || removed.length > 0 || qtyChanged.length > 0);
    }

    return wrap(
      String(field || 'Booking Updated'),
      'Booking details were updated.'
    );
  };

  return (
    <div className={isStaff ? "card booking-requests" : ""} id={tableId}>
      {customHeader ? (
        <div className="card-header">
          <h3>{customHeader}</h3>
        </div>
      ) : isStaff && (
        <div className="card-header">
          <h3>
            📅
            {isStaff ? 'Booking Requests' : displayMode === 'current' ? 'Current Bookings' : 'Previous Bookings'}
          </h3>
          <p>{isStaff ? 'Manage equipment booking requests' : displayMode === 'current' ? 'View your current and upcoming bookings' : 'View your completed bookings history'}</p>
        </div>
      )}
      <div className="card-content">
        {getEmptyMessage() ? (
          <div className="empty-message">
            {getEmptyMessage()}
          </div>
        ) : (
          <div className={displayMode === 'previous' ? 'previous-bookings' : ''}>
            {filteredBookings.map((booking) => {
              if (!booking || !booking.id) return null;
              console.log('Booking render:', { isStaff, status: booking.status, bookingId: booking.id });
              const addedEquipmentIds = getAddedEquipmentIds(booking);
              
              // Get class type styling
              const classTypeStyle = getClassTypeStyles(booking.className);
              // Card highlight color logic (only for staff/active bookings)
              let cardBg = undefined;
              if (isStaff && booking.status === 'Approved') {
                const classTypeKey = classTypeStyle.label ? classTypeStyle.label.toLowerCase().replace(/\s/g, '') : 'default';
                cardBg = classHighlightColors[classTypeKey] || classHighlightColors['default'];
              }
              
              return (
                <div key={booking.id} id={`booking-${booking.id}`} className="booking-card" style={
                  expandedBookings[booking.id]
                    ? { borderRadius: 12, boxShadow: '0 2px 8px 0 rgba(61,124,245,0.06)' }
                    : cardBg
                      ? { background: cardBg, borderRadius: 12, boxShadow: '0 2px 8px 0 rgba(61,124,245,0.06)' }
                      : {}
                }>
                  <div
                    className="booking-header"
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={() => toggleBookingExpand(booking.id)}
                    onMouseEnter={e => e.currentTarget.classList.add('chevron-hover')}
                    onMouseLeave={e => e.currentTarget.classList.remove('chevron-hover')}
                  >
                    <span
                      className="chevron-icon"
                      style={{
                        display: 'inline-flex',
                        transition: 'transform 0.25s cubic-bezier(.4,2,.6,1)',
                        marginRight: 16,
                        fontSize: 0,
                        transform: expandedBookings[booking.id] ? 'rotate(90deg)' : 'rotate(0deg)',
                        color: expandedBookings[booking.id] ? '#3d7cf5' : '#888',
                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))',
                      }}
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 6 15 12 9 18" />
                      </svg>
                    </span>
                    <div style={{ minWidth: 200 }}>
                      <div className="student-name" style={{ fontWeight: 700, fontSize: '1.15rem', color: '#fff' }}>{booking.studentName || 'Unknown'}</div>
                      <div className="student-id" style={{ fontSize: '0.95rem', color: '#aaa', marginTop: 2 }}>{booking.studentId || 'No ID'}</div>
                    </div>
                    {/* Project Details Section with dynamic class */}
                    {(booking.productionName || booking.className || booking.projectType) && (
                      <div className={`booking-project-details ${classTypeStyle.detailsClass}`} style={{ flex: 1, margin: '0 16px' }}>
                        {classTypeStyle.label && (
                          <div className={`class-badge ${classTypeStyle.badgeClass}`}>{classTypeStyle.label}</div>
                        )}
                        {booking.productionName && (
                          <div className="booking-project-item"><span className="booking-label">Production:</span> <span className="booking-value">{booking.productionName}</span></div>
                        )}
                        {booking.className && (
                          <div className="booking-project-item"><span className="booking-label">Class:</span> <span className="booking-value">{booking.className}</span></div>
                        )}
                        {booking.projectType && (
                          <div className="booking-project-item"><span className="booking-label">Project Type:</span> <span className="booking-value">{booking.projectType}</span></div>
                        )}
                        {/* File Links */}
                        {(booking.riskAssessmentUrl || booking.shootingScheduleUrl) && (
                          <div className="booking-files">
                            {booking.riskAssessmentUrl && (
                              <div className="booking-project-item"><span className="booking-label">Risk Assessment:</span> <a href={booking.riskAssessmentUrl} target="_blank" rel="noopener noreferrer" className="file-link">View Document</a></div>
                            )}
                            {booking.shootingScheduleUrl && (
                              <div className="booking-project-item"><span className="booking-label">Shooting Schedule:</span> <a href={booking.shootingScheduleUrl} target="_blank" rel="noopener noreferrer" className="file-link">View Document</a></div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Notes column - now shown for all bookings */}
                    <div className="booking-notes-col" style={{ marginLeft: 32, display: 'flex', alignItems: 'center', minWidth: 120, justifyContent: 'center', position: 'relative' }}>
                        <button
                          ref={el => noteButtonRefs.current[booking.id] = el}
                          className="notes-button"
                          onClick={e => { e.stopPropagation(); toggleNotes(booking.id); }}
                          title="View notes"
                          style={!booking.notes ? { backgroundColor: '#f59e0b', color: '#1f1f1f', borderColor: '#f59e0b' } : undefined}
                        >
                          <span className="notes-icon">📝</span>
                          <span className="notes-text">{booking.notes ? 'Notes' : 'Add Note'}</span>
                          {booking.notesUpdatedByStaff === false && (<span className="badge warning" style={{ marginLeft: '8px' }}>Updated!</span>)}
                        </button>
                        {activeNotes === booking.id && noteButtonRefs.current[booking.id] && ReactDOM.createPortal(
                          <div className="notes-popup" style={{ position: 'absolute', left: notePopupPos.left || 100, top: notePopupPos.top || 100, zIndex: 9999, minWidth: 220, marginTop: 8 }}>
                            <div className="notes-popup-header">
                              <h4>{isStaff ? 'Student Notes' : 'Notes'}</h4>
                              <button className="close-popup" onClick={e => { e.stopPropagation(); setActiveNotes(null); }}>×</button>
                            </div>
                            <div className="notes-popup-content">
                              {isStaff ? (
                                editingNotes === booking.id ? (
                                  <>
                                    <textarea className="notes-textarea" value={notesDraft} onChange={e => setNotesDraft(e.target.value)} />
                                    <div className="notes-actions">
                                      <button className="button primary small" onClick={() => handleSaveNotes(booking.id)}>Save</button>
                                      <button className="button secondary small" onClick={() => { setEditingNotes(null); setNotesDraft(booking.notes || ""); }}>Cancel</button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <p>{booking.notes || 'No notes yet.'}</p>
                                    <button className="button small" onClick={() => { setEditingNotes(booking.id); setNotesDraft(booking.notes || ""); }}>
                                      {booking.notes ? 'Edit Notes' : 'Add Note'}
                                    </button>
                                  </>
                                )
                              ) : (
                                <>
                                  {booking.notesUpdatedByStaff && (
                                    <span className="badge warning" style={{ marginBottom: 8 }}>Updated by Staff!</span>
                                  )}
                                  <p>{booking.notes || 'No notes yet.'}</p>
                                </>
                              )}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                    {/* Dates stacked vertically */}
                    <div className="booking-dates" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, marginLeft: 0, flex: 'unset' }}>
                      {isStaff && editingDates === booking.id ? (
                        <>
                          <div className="booking-date">
                            <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Start:</span>
                            <input
                              type="date"
                              name="startDate"
                              value={dateDraft.startDate}
                              onChange={handleDateChange}
                              className="date-input"
                            />
                          </div>
                          <div className="booking-date">
                            <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Return:</span>
                            <input
                              type="date"
                              name="endDate"
                              value={dateDraft.endDate}
                              onChange={handleDateChange}
                              className="date-input"
                            />
                          </div>
                          <div className="date-actions" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button
                              className="button primary small"
                              onClick={() => handleSaveDates(booking.id)}
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className="button secondary small"
                              onClick={cancelDateEditing}
                              disabled={isSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="booking-date">
                            <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Start:</span>
                            <span className="booking-value" style={{ fontWeight: 500, marginLeft: 6 }}>{new Date(booking.startDate).toLocaleDateString('en-GB')}</span>
                          </div>
                          <div className="booking-date">
                            <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Return:</span>
                            <span className="booking-value" style={{ fontWeight: 500, marginLeft: 6 }}>{new Date(booking.endDate).toLocaleDateString('en-GB')}</span>
                          </div>
                          {booking.returnedDate && (
                            <div className="booking-date">
                              <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Returned:</span>
                              <span className="booking-value" style={{ fontWeight: 500, marginLeft: 6 }}>{new Date(booking.returnedDate).toLocaleDateString('en-GB')}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {/* Status */}
                    <div className="booking-status" style={{ marginLeft: 16, alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                      <span className={booking.status === 'Approved' ? 'badge success' : booking.status === 'Returned' ? 'badge success' : 'badge pending'}>{booking.status}</span>
                      {isStaff && booking.hasUnreviewedEdits && (
                        <span className="badge warning">Edited</span>
                      )}
                      {/* Approve/Reject buttons for staff on pending bookings, now stacked vertically */}
                      {isStaff && booking.status === 'Pending' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginLeft: 8 }}>
                          <button
                            className="button primary small animate-fade-in"
                            onClick={e => { e.stopPropagation(); approveBooking(booking.id); }}
                          >
                            Approve
                          </button>
                          <button
                            className="button danger small animate-fade-in"
                            onClick={e => { e.stopPropagation(); rejectBooking(booking.id); }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {expandedBookings[booking.id] && (
                    <div className={`expand-section${expandedBookings[booking.id] ? ' expanded' : ''}`}>
                      <div
                        style={{
                          padding: expandedBookings[booking.id] ? 16 : 0,
                          opacity: expandedBookings[booking.id] ? 1 : 0,
                          transition: 'opacity 0.25s, padding 0.25s',
                          pointerEvents: expandedBookings[booking.id] ? 'auto' : 'none',
                          height: expandedBookings[booking.id] ? 'auto' : 0,
                        }}
                      >
                        <div className="booking-equipment">
                          <h4>Equipment</h4>
                          {!isStaff && studentEditBookingId === booking.id && studentEditDraft && (
                            <div style={{ marginBottom: 12, padding: 12, border: '1px solid #555', borderRadius: 8 }}>
                              <h4 style={{ marginBottom: 12 }}>Edit Booking</h4>
                              <div className="form-group">
                                <label>Start Date</label>
                                <input
                                  type="date"
                                  value={studentEditDraft.startDate}
                                  onChange={(e) => handleStudentDraftChange('startDate', e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label>End Date</label>
                                <input
                                  type="date"
                                  value={studentEditDraft.endDate}
                                  onChange={(e) => handleStudentDraftChange('endDate', e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label>Notes</label>
                                <textarea
                                  className="notes-textarea"
                                  value={studentEditDraft.notes}
                                  onChange={(e) => handleStudentDraftChange('notes', e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label>Add Equipment</label>
                                <div style={{ display: 'grid', gap: 8 }}>
                                  <input
                                    type="text"
                                    placeholder="Search by name, type, or category"
                                    value={studentEquipmentSearch}
                                    onChange={(e) => setStudentEquipmentSearch(e.target.value)}
                                  />
                                  <select
                                    value={studentAddEquipmentId}
                                    onChange={(e) => setStudentAddEquipmentId(e.target.value)}
                                  >
                                    <option value="">Select equipment...</option>
                                    {studentAddEquipmentGroups.map((group) => (
                                      <optgroup key={group.category} label={group.category}>
                                        {group.items.map((item) => (
                                          <option key={item.id} value={item.id}>
                                            {item.name}{item.type ? ` (${item.type})` : ''}
                                          </option>
                                        ))}
                                      </optgroup>
                                    ))}
                                  </select>
                                  {studentAddEquipmentGroups.length > 0 && (
                                    <div style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid #444', borderRadius: 6, padding: 6 }}>
                                      {studentAddEquipmentGroups.map((group) => (
                                        <div key={group.category} style={{ marginBottom: 6 }}>
                                          <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 4 }}>{group.category}</div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {group.items.slice(0, 8).map((item) => (
                                              <button
                                                key={`${group.category}-${item.id}`}
                                                type="button"
                                                className="button secondary small"
                                                onClick={() => setStudentAddEquipmentId(item.id)}
                                                style={{
                                                  opacity: studentAddEquipmentId === item.id ? 1 : 0.85,
                                                  borderColor: studentAddEquipmentId === item.id ? '#4fc3f7' : undefined
                                                }}
                                              >
                                                {item.name}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {studentAddEquipmentGroups.length === 0 && (
                                    <div style={{ fontSize: '0.85rem', color: '#aaa' }}>
                                      No matching equipment found.
                                    </div>
                                  )}
                                  <button
                                    className="button secondary small"
                                    onClick={handleStudentAddItem}
                                    type="button"
                                    disabled={!studentAddEquipmentId}
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                              {studentEditError && (
                                <div className="error-message">{studentEditError}</div>
                              )}
                            </div>
                          )}
                          <div className="equipment-by-category">
                            {Object.entries(
                              groupBookingItemsByCategory(
                                editingEquipment === booking.id
                                  ? modifiedItems
                                  : studentEditBookingId === booking.id
                                    ? studentEditDraft?.items
                                    : booking.items
                              )
                            ).map(([category, items]) => (
                              <div key={category} className="equipment-category">
                                <h5>{category}</h5>
                                <div className="equipment-table-wrapper">
                                  <table className="equipment-table">
                                    <thead>
                                      <tr>
                                        <th>Item</th>
                                        <th>Type</th>
                                        <th>Qty</th>
                                        {isStaff && booking.status === 'Approved' && <th>Out</th>}
                                        {isStaff && booking.status === 'Approved' && <th>In</th>}
                                        {(editingEquipment === booking.id || studentEditBookingId === booking.id) && <th>Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((item) => (
                                        <tr key={`${booking.createdAt}-${item.equipmentId}`} className={
                                          pendingRemoval.includes(item.equipmentId)
                                            ? 'pending-removal'
                                            : pendingAdd.includes(item.equipmentId)
                                            ? 'pending-add'
                                            : ''
                                        } style={isStaff && addedEquipmentIds.has(item.equipmentId) ? { background: staffEditHighlightBg } : undefined}>
                                          <td>{item.equipmentName}
                                            {pendingAdd.includes(item.equipmentId) && (
                                              <span className="new-badge">New</span>
                                            )}
                                            {isStaff && addedEquipmentIds.has(item.equipmentId) && (
                                              <span className="badge warning" style={{ marginLeft: 8 }}>Added</span>
                                            )}
                                          </td>
                                          <td>{item.type || '-'}</td>
                                          <td>
                                            {editingEquipment === booking.id || studentEditBookingId === booking.id ? (
                                              <input
                                                type="number"
                                                min="1"
                                                value={
                                                  studentEditBookingId === booking.id
                                                    ? (studentEditDraft?.items?.find(mi => mi.equipmentId === item.equipmentId)?.quantity || item.quantity)
                                                    : (modifiedItems?.find(mi => mi.equipmentId === item.equipmentId)?.quantity || item.quantity)
                                                }
                                                onChange={(e) => (
                                                  studentEditBookingId === booking.id
                                                    ? handleStudentItemQuantityChange(item.equipmentId, e.target.value)
                                                    : handleEquipmentQuantityChange(item.equipmentId, parseInt(e.target.value))
                                                )}
                                                className="quantity-input"
                                              />
                                            ) : (
                                              item.quantity
                                            )}
                                          </td>
                                          {isStaff && booking.status === 'Approved' && (
                                            <>
                                              <td>
                                                <div className="checkbox-container">
                                                  <input
                                                    type="checkbox"
                                                    id={`checkout-${booking.id}-${item.equipmentId}`}
                                                    checked={isCheckedOut(booking.id, item.equipmentId)}
                                                    onChange={(e) => handleCheckout(booking.id, item.equipmentId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                  />
                                                </div>
                                              </td>
                                              <td>
                                                <div className="checkbox-container">
                                                  <input
                                                    type="checkbox"
                                                    id={`checkin-${booking.id}-${item.equipmentId}`}
                                                    checked={isCheckedIn(booking.id, item.equipmentId)}
                                                    onChange={(e) => handleCheckin(booking.id, item.equipmentId)}
                                                    disabled={!isCheckedOut(booking.id, item.equipmentId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                  />
                                                </div>
                                              </td>
                                            </>
                                          )}
                                          {editingEquipment === booking.id && (
                                            <td>
                                              <button
                                                className="button danger small"
                                                onClick={() => handleRemoveEquipment(item.equipmentId)}
                                                title="Remove equipment from booking"
                                                disabled={pendingRemoval.includes(item.equipmentId)}
                                              >
                                                {pendingRemoval.includes(item.equipmentId) ? 'Pending...' : 'Remove'}
                                              </button>
                                            </td>
                                          )}
                                          {studentEditBookingId === booking.id && (
                                            <td>
                                              <button
                                                className="button danger small"
                                                onClick={() => handleStudentRemoveItem(item.equipmentId)}
                                                type="button"
                                              >
                                                Remove
                                              </button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                      {editingEquipment === booking.id && items.length === 0 && (
                                        <tr>
                                          <td colSpan={6} style={{ textAlign: 'center', color: '#aaa' }}>No equipment in this category.</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                                {isStaff && booking.status === 'Approved' && (() => {
                                  console.log('Modify Equipment button render:', { isStaff, status: booking.status, bookingId: booking.id });
                                  return null;
                                })()}
                                {isStaff && booking.status === 'Approved' && (
                                  editingEquipment === booking.id ? (
                                    <>
                                      <select
                                        className="equipment-select"
                                        onChange={(e) => handleAddEquipment(e.target.value)}
                                        value=""
                                      >
                                        <option value="">Add Equipment...</option>
                                        {Object.entries(
                                          equipmentData
                                            .filter(e => !modifiedItems?.some(mi => mi.equipmentId === e.id))
                                            .reduce((acc, equipment) => {
                                              if (!acc[equipment.category]) {
                                                acc[equipment.category] = [];
                                              }
                                              acc[equipment.category].push(equipment);
                                              return acc;
                                            }, {})
                                        ).map(([category, items]) => (
                                          <optgroup key={category} label={category}>
                                            {items.map(e => (
                                              <option key={e.id} value={e.id}>
                                                {e.name} ({e.availableQty} available)
                                              </option>
                                            ))}
                                          </optgroup>
                                        ))}
                                      </select>
                                      <button
                                        className="button primary small"
                                        onClick={() => handleSaveEquipmentModifications(booking.id)}
                                        disabled={isSaving}
                                      >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                      </button>
                                      <button
                                        className="button secondary small"
                                        onClick={cancelEquipmentModifications}
                                        disabled={isSaving}
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      className="button secondary small"
                                      onClick={() => handleModifyEquipment(booking)}
                                    >
                                      Modify Equipment
                                    </button>
                                  )
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        {isStaff && booking.status === 'Approved' && booking.items && Array.isArray(booking.items) && booking.items.length > 0 && (
                          <div className="booking-footer">
                            <div className="checkout-summary">
                              <span className="summary-label">Equipment Status:</span>
                              {booking.items.every(item => isCheckedOut(booking.id, item.equipmentId) && isCheckedIn(booking.id, item.equipmentId)) ? (
                                <span className="badge success">All Items Returned</span>
                              ) : booking.items.every(item => !isCheckedOut(booking.id, item.equipmentId)) ? (
                                <span className="badge pending">Pending Checkout</span>
                              ) : booking.items.some(item => isCheckedOut(booking.id, item.equipmentId) && !isCheckedIn(booking.id, item.equipmentId)) ? (
                                <span className="badge warning">Some Items Out</span>
                              ) : null}
                            </div>
                            <div className="booking-actions-footer">
                              {isStaff && booking.status === 'Approved' && !editingDates && (
                                <button
                                  className="button secondary animate-fade-in"
                                  onClick={() => handleEditDates(booking)}
                                >
                                  Edit Dates
                                </button>
                              )}
                              <button
                                className="button warning animate-fade-in"
                                onClick={async () => {
                                  // Check out all items
                                  if (!booking.items) return;
                                  
                                  const newCheckedOut = {...checkedOutItems};
                                  if (!newCheckedOut[booking.id]) newCheckedOut[booking.id] = {};
                                  
                                  booking.items.forEach(item => {
                                    newCheckedOut[booking.id][item.equipmentId] = true;
                                  });
                                  
                                  setCheckedOutItems(newCheckedOut);
                                  await handleSaveCheckStatus(booking.id, null);
                                }}
                                disabled={isSaving}
                              >
                                {isSaving ? 'Saving...' : 'Check Out All'}
                              </button>
                              <button
                                className="button info-dark animate-fade-in"
                                onClick={async () => {
                                  // Check in all items that are checked out
                                  if (!booking.items) return;
                                  
                                  const newCheckedIn = {...checkedInItems};
                                  if (!newCheckedIn[booking.id]) newCheckedIn[booking.id] = {};
                                  
                                  booking.items.forEach(item => {
                                    if (isCheckedOut(booking.id, item.equipmentId)) {
                                      newCheckedIn[booking.id][item.equipmentId] = true;
                                    }
                                  });
                                  
                                  setCheckedInItems(newCheckedIn);
                                  await handleSaveCheckStatus(booking.id, null);
                                }}
                                disabled={isSaving || !booking.items || !booking.items.some(item => isCheckedOut(booking.id, item.equipmentId) && !isCheckedIn(booking.id, item.equipmentId))}
                              >
                                {isSaving ? 'Saving...' : 'Check In All'}
                              </button>
                              <button
                                className="button primary animate-fade-in"
                                onClick={() => returnEquipment(booking.id)}
                              >
                                Mark as Returned
                              </button>
                            </div>
                          </div>
                        )}
                        {!isStaff && displayMode === 'current' && (
                          <div className="booking-footer">
                            {studentEditBookingId === booking.id ? (
                              <div className="booking-actions-footer">
                                <button className="button primary" onClick={() => saveStudentEdit(booking)} disabled={isSaving}>
                                  {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button className="button secondary" onClick={cancelStudentEdit} disabled={isSaving}>
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="booking-actions-footer">
                                <button
                                  className="button secondary"
                                  onClick={() => startStudentEdit(booking)}
                                  disabled={!canStudentEditBooking(booking)}
                                >
                                  Edit Booking
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {isStaff && Array.isArray(booking.changeLog) && booking.changeLog.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <h4>Change History</h4>
                            {[...booking.changeLog]
                              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                              .map((entry, idx) => (
                                <div key={`${booking.id}-changelog-${idx}`}>
                                  {renderChangeEntry(entry)}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedBookingsTable;
